import React, { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, XCircle } from 'lucide-react';
import { PageHeader } from '../../../components/common/PageHeader';
import { InfoItem } from '../../../components/common/InfoItem';
import { StatusBadge } from '../../../components/common/StatusBadge';
import { RoleSwitcher } from '../../../components/admin/review/RoleSwitcher';
import { SummaryTab } from '../../../components/admin/review/SummaryTab';
import { OwnerReviewTab } from '../../../components/admin/review/OwnerReviewTab';
import { VehicleReviewTab } from '../../../components/admin/review/VehicleReviewTab';
import { DocumentReviewTab } from '../../../components/admin/review/DocumentReviewTab';
import { BrtaCompareTab } from '../../../components/admin/review/BrtaCompareTab';
import { DriverReviewTab } from '../../../components/admin/review/DriverReviewTab';
import { ValidationTab } from '../../../components/admin/review/ValidationTab';
import { HistoryTab } from '../../../components/admin/review/HistoryTab';
import { ReturnForCorrectionModal } from '../../../components/admin/review/ReturnForCorrectionModal';
import { RejectApplicationModal } from '../../../components/admin/review/RejectApplicationModal';
import { AssignOfficerModal } from '../../../components/admin/review/AssignOfficerModal';
import { useOwnerService } from '../../../contexts/OwnerServiceContext';
import { useApplicationReview } from '../../../contexts/ApplicationReviewContext';
import { brtaBadge, isBlockingDuplicate, findDuplicate, queueMeta, queueOf } from '../../../utils/applicationQueue';
import type { DetailTab } from '../../../types/review';
const tabs: {
  key: DetailTab;
  label: string;
}[] = [{
  key: 'summary',
  label: 'আবেদন সারসংক্ষেপ'
}, {
  key: 'owner',
  label: 'মালিকের তথ্য'
}, {
  key: 'vehicle',
  label: 'গাড়ির তথ্য'
}, {
  key: 'docs',
  label: 'সংযুক্ত ডকুমেন্ট'
}, {
  key: 'brta',
  label: 'BRTA তথ্য যাচাই'
}, {
  key: 'drivers',
  label: 'চালকের তথ্য'
}, {
  key: 'validation',
  label: 'যাচাই ও অনুমোদন'
}, {
  key: 'history',
  label: 'কার্যক্রমের ইতিহাস'
}];
export function ApplicationDetail() {
  const {
    id
  } = useParams();
  const [params, setParams] = useSearchParams();
  const {
    applications
  } = useOwnerService();
  const {
    getReview
  } = useApplicationReview();
  const [modal, setModal] = useState<'return' | 'reject' | 'assign' | null>(null);
  const app = applications.find(a => a.id === id);
  if (!app) {
    return <div className="rounded-lg border border-border bg-white p-10 text-center" data-id="element-3242" data-mp="546s8vknvipm">
        <p className="font-semibold text-navy-900" data-id="element-3243" data-mp="53nzdci9gip">আবেদন পাওয়া যায়নি</p>
        <Link to="/admin/owner/applications" className="mt-2 inline-block text-sm text-navy-700 hover:underline" data-id="element-3244" data-mp="76m6kntafyi">আবেদন তালিকায় ফিরে যান</Link>
      </div>;
  }
  const tab = params.get('tab') as DetailTab | null ?? 'summary';
  const goTab = (t: DetailTab) => {
    setParams(t === 'summary' ? {} : {
      tab: t
    });
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const r = getReview(app);
  const q = queueOf(app);
  const brta = brtaBadge(app);
  const dup = findDuplicate(app, applications);
  const blocking = isBlockingDuplicate(app, applications);
  const lastCorrection = r.corrections[r.corrections.length - 1];
  return <div data-id="element-3245" data-mp="1a4eqpybs2cq2">
      <Link to="/admin/owner/applications" className="mb-2 inline-flex items-center gap-1 text-sm font-medium text-navy-700 hover:underline" data-id="element-3246" data-mp="1a450qaqqg5kd"><ArrowLeft className="h-4 w-4" data-id="element-3247" data-mp="1f4j074e9q76s" />আবেদন তালিকা</Link>
      <PageHeader title={`আবেদন বিস্তারিত — ${app.id}`} subtitle="মালিকের মোবাইল অ্যাপ থেকে জমাকৃত আবেদন" actions={<RoleSwitcher data-id="element-3249" data-mp="1o4fqtbk7xg4h" />} data-id="element-3248" data-mp="1j307pp1lpjoq" />

      <section className="mb-4 grid gap-x-6 gap-y-3 rounded-lg border border-border bg-white px-5 py-4 shadow-sm sm:grid-cols-3 lg:grid-cols-6" aria-label="আবেদনের মূল তথ্য" data-id="element-3250" data-mp="1o11mhxrzuwue">
        <InfoItem label="আবেদনের অবস্থা" value={<StatusBadge tone={queueMeta[q].tone} data-id="element-3252" data-mp="3utklanzcyy1k">{queueMeta[q].label}</StatusBadge>} data-id="element-3251" data-mp="3pw2aes1s68qx" />
        <InfoItem label="জমার তারিখ" value={app.submitted} data-id="element-3253" data-mp="3uvopl7165z2d" />
        <InfoItem label="মালিকের নাম" value={app.ownerName} data-id="element-3254" data-mp="1p48qn4dest2r" />
        <InfoItem label="গাড়ির রেজিস্ট্রেশন" value={app.vehicleReg} data-id="element-3255" data-mp="1k6h2mlfvxr3u" />
        <InfoItem label="দায়িত্বপ্রাপ্ত কর্মকর্তা" value={r.assignedOfficer ?? 'নির্ধারিত হয়নি'} data-id="element-3256" data-mp="1k8be7959o6v9" />
        <InfoItem label="BRTA যাচাই" value={<StatusBadge tone={brta.tone} data-id="element-3258" data-mp="1u3v7dnsg4rgb">{brta.label}</StatusBadge>} data-id="element-3257" data-mp="1y3vurxxiwczi" />
      </section>

      {dup && <div className={`mb-4 flex items-start gap-2.5 rounded-lg border px-4 py-3 text-sm ${blocking ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-900'}`} data-id="element-3259" data-mp="16eaof4walu71">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" data-id="element-3260" data-mp="1f7noqrwory35" />
          <p data-id="element-3261" data-mp="1f98joimwxj1j">
            <b data-id="element-3262" data-mp="1agfddqbsq2nc">{blocking ? 'ডুপ্লিকেট রেজিস্ট্রেশন — মীমাংসা ছাড়া অনুমোদন করা যাবে না।' : 'একই রেজিস্ট্রেশন নম্বরে অপর আবেদন রয়েছে।'}</b>{' '}
            <Link to={`/admin/owner/applications/${dup.id}`} className="font-semibold underline" data-id="element-3263" data-mp="mprts7sipoky">{dup.id}</Link> ({dup.ownerName})
          </p>
        </div>}
      {q === 'returned' && lastCorrection && <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-id="element-3264" data-mp="8jq4y45mkdc8">
          <b data-id="element-3265" data-mp="ddc7h3i2r0ub">সংশোধনের জন্য ফেরত পাঠানো হয়েছে</b> • {lastCorrection.date} • মালিকের পুনরায় জমার অপেক্ষায়
          <p className="mt-1" data-id="element-3266" data-mp="3uqem580awzls">নির্দেশনা: {lastCorrection.instruction}</p>
        </div>}
      {q === 'rejected' && <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-id="element-3267" data-mp="rbsopodkc9qp">
          <XCircle className="mt-0.5 h-4 w-4 shrink-0" data-id="element-3268" data-mp="wb5dienzvqvx" />
          <p data-id="element-3269" data-mp="w8j5yf13tbdp"><b data-id="element-3270" data-mp="w8j409hjn1b2">আবেদন বাতিল</b> — {app.correctionReason}</p>
        </div>}

      <div className="no-scrollbar mb-4 flex gap-1 overflow-x-auto rounded-lg border border-border bg-white px-2 shadow-sm" role="tablist" aria-label="আবেদনের বিভাগ" data-id="element-3271" data-mp="hdcyroq9v6is">
        {tabs.map(t => <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => goTab(t.key)} className={`shrink-0 border-b-2 px-3.5 py-3 text-[13px] font-medium transition ${tab === t.key ? 'border-navy-800 text-navy-900' : 'border-transparent text-slate-500 hover:text-navy-800'}`} data-id="element-3272" data-mp="322ir07qdrfr4">
            {t.label}
          </button>)}
      </div>

      {tab === 'summary' && <SummaryTab app={app} goTab={goTab} onAssign={() => setModal('assign')} onReturn={() => setModal('return')} data-id="element-3273" data-mp="yvtkc1ts0ttd" />}
      {tab === 'owner' && <OwnerReviewTab app={app} data-id="element-3274" data-mp="tx08edu51avb" />}
      {tab === 'vehicle' && <VehicleReviewTab app={app} data-id="element-3275" data-mp="puvcf8g7w25i" />}
      {tab === 'docs' && <DocumentReviewTab app={app} data-id="element-3276" data-mp="ptby3ne4g16d" />}
      {tab === 'brta' && <BrtaCompareTab app={app} data-id="element-3277" data-mp="kuqvxifb77fa" />}
      {tab === 'drivers' && <DriverReviewTab app={app} data-id="element-3278" data-mp="kxdeed3qn7ca" />}
      {tab === 'validation' && <ValidationTab app={app} goTab={goTab} onReturn={() => setModal('return')} onReject={() => setModal('reject')} data-id="element-3279" data-mp="fxr0e1g35n2f" />}
      {tab === 'history' && <HistoryTab app={app} data-id="element-3280" data-mp="2f779736ivltn" />}

      <ReturnForCorrectionModal app={modal === 'return' ? app : null} onClose={() => setModal(null)} initialReasons={app.brtaResult === 'অমিল' ? ['BRTA তথ্যের অমিল'] : []} data-id="element-3281" data-mp="2a9yn4ri96xn4" />
      <RejectApplicationModal app={modal === 'reject' ? app : null} onClose={() => setModal(null)} data-id="element-3282" data-mp="2mt2razdno62i" />
      <AssignOfficerModal app={modal === 'assign' ? app : null} onClose={() => setModal(null)} data-id="element-3283" data-mp="2rjsuc5ylzwu1" />
    </div>;
}